import {
  Stack,
  Dialog,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { db } from "firebaseConfig";
import firebase from "firebase/compat";
import {
  ListingPicturesVisibilityControls,
  LISTING_PICTURES_VISIBILITY
} from "./ListingPicturesVisibilityControls";
import { ListingWatermarkPanel } from "../watermarks/ListingWatermarkPanel";
import type { ListingPicturesVisibility } from "./ListingPicturesVisibilityControls";
import type { Listing } from "../../../@types/listings/listing";
import type { FavoriteListingsOfContact } from "../../../@types/sharedSchema";

export { LISTING_PICTURES_VISIBILITY };
export type { ListingPicturesVisibility };

type ListingPicturesVisibilityProps = Pick<Listing, "pictures"> & {
  open: boolean;
  listingId: string;
  favoriteListingOfContact: FavoriteListingsOfContact;
  onClose: () => void;
};

export function ListingPicturesVisibilityDialog({
  open,
  pictures,
  listingId,
  favoriteListingOfContact,
  onClose
}: ListingPicturesVisibilityProps): JSX.Element {
  const picturesVisibilityMode = getListingPicturesVisibility(listingId, favoriteListingOfContact);

  const { additionalDetails } = favoriteListingOfContact ?? {};

  const parsedBlurredPictures = additionalDetails?.[listingId]?.hiddenPictures?.pictures ?? [];

  const { t: translate } = useTranslation();

  const indexEnabledFieldKey = getStringHiddenListingPicturesFieldsIndex(listingId, "enabled");
  const indexHiddenPicturesFieldKey = getStringHiddenListingPicturesFieldsIndex(
    listingId,
    "pictures"
  );

  const handleTogglePicture = (image: string): void => {
    const isPictureAlreadyBlurred = parsedBlurredPictures.includes(image);

    db.collection("favorite-listings-of-contact")
      .doc(favoriteListingOfContact.id)
      .update({
        [indexHiddenPicturesFieldKey]: isPictureAlreadyBlurred
          ? firebase.firestore.FieldValue.arrayRemove(image)
          : firebase.firestore.FieldValue.arrayUnion(image)
      });
  };

  /**
   * ⚠️ THE MODE AND THE HIDDEN SET ARE INDEPENDENT. Switching to "show all" or "hide all" must NOT
   * clear `hiddenPictures` — it used to write `[]` on both, so a realtor who had hand-picked eight
   * photos to hide, glanced at "show all", and switched back lost all eight with no undo. The
   * `enabled` flag alone decides whether the set is applied (`getListingMatchAdditionalDetails`
   * reads the pictures only when it is true), so the set can simply be left alone and comes back
   * intact when they return to "hide some".
   *
   * This matters far more once presentation is stored per (realtor, listing) rather than per
   * contact: there, one stray click would wipe the curation for every customer at once.
   */
  const handleModeChange = async (newMode: ListingPicturesVisibility): Promise<void> => {
    const doc = db.collection("favorite-listings-of-contact").doc(favoriteListingOfContact.id);
    if (newMode === "all") {
      await doc.update({
        listingIdsWithPicturesHidden: firebase.firestore.FieldValue.arrayRemove(listingId),
        [indexEnabledFieldKey]: false
      });
    } else if (newMode === "some") {
      await doc.update({
        listingIdsWithPicturesHidden: firebase.firestore.FieldValue.arrayRemove(listingId),
        [indexEnabledFieldKey]: true
      });
    } else {
      await doc.update({
        listingIdsWithPicturesHidden: firebase.firestore.FieldValue.arrayUnion(listingId),
        [indexEnabledFieldKey]: false
      });
    }
  };

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <DialogTitle sx={{ py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">
            {translate("listingMatchToContactsPage.listingPicturesVisibility")}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {/* Per-listing watermark removal, in the body rather than the title: the old
            ListingWatermarkStatusChip returned `null` while the listing doc loaded and
            for listings with no marketplace doc, so the entry point silently vanished. */}
        <ListingWatermarkPanel listingId={listingId} />
        <Stack sx={{ pt: 2 }}>
          <ListingPicturesVisibilityControls
            pictures={pictures}
            mode={picturesVisibilityMode}
            hiddenPictures={parsedBlurredPictures}
            onModeChange={handleModeChange}
            onTogglePicture={handleTogglePicture}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export function getStringListingHiddenPicturesIndex(listingId: string): string {
  return `additionalDetails.${listingId}.hiddenPictures`;
}

export function getStringHiddenListingPicturesFieldsIndex(
  listingId: string,
  field: keyof FavoriteListingsOfContact["additionalDetails"][string]["hiddenPictures"]
): string {
  const indexListingPicturesKey = getStringListingHiddenPicturesIndex(listingId);

  return `${indexListingPicturesKey}.${field}`;
}

export function getListingPicturesVisibility(
  listingId: string,
  favoriteListingOfContact: FavoriteListingsOfContact
): ListingPicturesVisibility {
  const { listingIdsWithPicturesHidden, additionalDetails } = favoriteListingOfContact ?? {};

  const isAllPicturesHidden = listingIdsWithPicturesHidden?.includes(listingId);

  if (isAllPicturesHidden) return "none";

  const isSomePicturesHidden = additionalDetails?.[listingId]?.hiddenPictures?.enabled;

  if (isSomePicturesHidden) return "some";

  return "all";
}
